import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MoreVert,
  SearchOutlined,
  UploadOutlined,
} from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { InputText } from "primereact/inputtext";
import { Workbook } from "exceljs";
import * as XLSX from "xlsx";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { ThemeProvider } from "@mui/material/styles";
import {
  DataDialog,
  DataPagination,
  DataTable,
  Helmet,
  PageTitle,
} from "@app/components/atoms";
import { IconPlus } from "@app/components/icons";
import { ConfirmDialog } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/ConfirmDialog";
import {
  CURRENT_PAGE,
  DialogTypes,
  LIMIT_PAGE,
  MaintenanceWorkType,
  SortOrderType,
  TOTAL_PAGE,
  countryCodeOptions,
} from "@app/constants";
import { createAppTheme } from "@app/theme/muiTheme";
import { capitalize } from "@app/helpers";
import { setIsLoading, setShowAlert, store } from "@app/store";
import { PaginationData, theme } from "@app/types";
import {
  apiDeletePeriodicMaintenanceMasterData,
  apiGetMaintenanceReportTemplates,
  apiGetPeriodicMaintenanceMasterData,
  apiGetPeriodicMaintenanceMasterDataRowDetail,
  apiGetPeriodicMaintenanceTemplateFileUrl,
  apiPostPeriodicMaintenanceBulkImport,
  apiUpdatePeriodicMaintenanceMasterData,
} from "@app/api";
import type {
  PeriodicMaintenanceMasterDataRow,
  PeriodicMaintenanceMasterDataRowDetail,
} from "@app/types/maintenanceMasterData.types";

const DEFAULT_SEARCH_TEXT = "";
const DEFAULT_SEARCH_INPUT = "";
const DEFAULT_FILTER = "all";
const SMALL_SCREEN_BREAKPOINT = "sm";
const DEFAULT_ORDER_BY = "model";

const WORK_TYPES: string[] = Object.values(MaintenanceWorkType);

const MANUAL_ENTRY_WORK_TYPES: string[] = WORK_TYPES.filter(
  (w) => w !== MaintenanceWorkType.REFILL,
);

const RESIDENCE_COUNTRY_CODES: string[] = countryCodeOptions.map(
  (opt) => opt.value,
);

const LOCALE_COLUMNS: { key: string; labelKey: string }[] = [
  { key: "en", labelKey: "translation.english" },
  { key: "ja", labelKey: "translation.japanese" },
  { key: "ar", labelKey: "translation.arabic" },
  { key: "es", labelKey: "translation.spanish" },
  { key: "fr", labelKey: "translation.french" },
  { key: "km", labelKey: "translation.khmer" },
  { key: "lo", labelKey: "translation.lao" },
  { key: "pt", labelKey: "translation.portuguese" },
  { key: "ru", labelKey: "translation.russian" },
  { key: "th", labelKey: "translation.thai" },
  { key: "ur", labelKey: "translation.urdu" },
  { key: "vi", labelKey: "translation.vietnamese" },
];

const DIALOG_LOCALE_COLUMNS = [
  ...LOCALE_COLUMNS.filter(({ key }) => key !== "ar" && key !== "ur"),
  ...LOCALE_COLUMNS.filter(({ key }) => key === "ar" || key === "ur"),
];

const BASE_COLUMN_KEYS = [
  "model",
  "type",
  "itemCode",
  "intervalCode",
  "work",
  "countryCode",
] as const;
const COLUMN_ORDER: string[] = [
  ...BASE_COLUMN_KEYS,
  ...LOCALE_COLUMNS.map((c) => c.key),
];
const COUNTRY_CODE_COLUMN_INDEX = BASE_COLUMN_KEYS.indexOf("countryCode") + 1;
const INTERVAL_CODE_COLUMN_INDEX = BASE_COLUMN_KEYS.indexOf("intervalCode") + 1;
const WORK_COLUMN_INDEX = BASE_COLUMN_KEYS.indexOf("work") + 1;

const TEMPLATE_SHEET_NAME = "Periodic Maintenance";
const TEMPLATE_LEGEND_SHEET_NAME = "Country Codes";
const TEMPLATE_INTERVAL_LEGEND_SHEET_NAME = "Interval Codes";
const TEMPLATE_PASSWORD = "2286";
const TEMPLATE_DATA_ROW_COUNT = 5000;

const FIELD_BORDER_TRANSITION_SX = {
  "& .MuiOutlinedInput-notchedOutline": {
    transition: "border-color 150ms ease-in-out",
  },
};
const SELECT_FIELD_SX = {
  margin: "8px 0",
  ...FIELD_BORDER_TRANSITION_SX,
};

const rtlFieldCache = createCache({
  key: "muirtl-pm-field",
  stylisPlugins: [prefixer, rtlPlugin],
});
const rtlFieldTheme = createAppTheme("rtl");

type EditableRow = {
  model: string;
  type: string;
  itemCode: string;
  intervalCode: string;
  work: string;
  countryCode: string;
  en: string;
  ja?: string;
};

type PreviewRow = {
  id: number;
  model: string;
  type: string;
  itemCode: string;
  intervalCode: string;
  work: string;
  countryCode: string;
  [localeCode: string]: any;
  _errors: string[];
  _isDuplicate: boolean;
  _isFailed?: boolean;
  _failureReason?: string;
};

export function ManagePeriodicMaintenance() {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(SMALL_SCREEN_BREAKPOINT),
  );

  const [page, setPage] = useState(CURRENT_PAGE);
  const [limit] = useState(LIMIT_PAGE);
  const [order, setOrder] = useState<SortOrderType | undefined>(undefined);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [searchInput, setSearchInput] = useState(DEFAULT_SEARCH_INPUT);
  const [searchText, setSearchText] = useState(DEFAULT_SEARCH_TEXT);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [work, setWork] = useState<string>(DEFAULT_FILTER);
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_FILTER);
  const [rows, setRows] = useState<PeriodicMaintenanceMasterDataRow[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState<PaginationData>({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });

  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkPaginationModel, setBulkPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [failedCount, setFailedCount] = useState(0);

  const [optionsMenuAnchor, setOptionsMenuAnchor] =
    useState<null | HTMLElement>(null);
  const openOptionsMenu = Boolean(optionsMenuAnchor);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleCloseOptionsMenu = () => setOptionsMenuAnchor(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] =
    useState<PeriodicMaintenanceMasterDataRowDetail | null>(null);
  const [editErrors, setEditErrors] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const markFieldTouched = (field: string) =>
    setTouchedFields((prev) => ({ ...prev, [field]: true }));

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [cancelEditConfirmOpen, setCancelEditConfirmOpen] = useState(false);
  const [intervalChoiceOptions, setIntervalChoiceOptions] = useState<
    { code: string; name: string }[]
  >([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiGetMaintenanceReportTemplates();
        const templates =
          res.data?.data?.regularMaintenanceItemChoiceTemplates ?? [];
        setIntervalChoiceOptions(
          templates
            .filter(
              (choice: any) =>
                choice.regularMaintenanceItemChoiceCode !== "OTHER",
            )
            .map((choice: any) => ({
              code: choice.regularMaintenanceItemChoiceCode,
              name: choice.regularMaintenanceItemChoiceName,
            })),
        );
      } catch (err) {
        console.error("Error fetching maintenance report templates:", err);
      }
    })();
  }, []);

  const filteredPreviewRows = useMemo(
    () =>
      previewRows.filter((row) => {
        if (!bulkSearch.trim()) return true;
        const q = bulkSearch.toLowerCase();
        return Object.values(row).some((val) =>
          String(val ?? "")
            .toLowerCase()
            .includes(q),
        );
      }),
    [previewRows, bulkSearch],
  );

  const hasBlockingRows = useMemo(
    () => previewRows.some((row) => row._errors.length > 0 || row._isDuplicate),
    [previewRows],
  );

  const renderValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === "") {
      return <span style={{ color: "#000", opacity: 1 }}>-</span>;
    }
    return <span style={{ color: "#000", opacity: 1 }}>{value}</span>;
  }, []);

  const countryNameByCode = useMemo(
    () =>
      Object.fromEntries(
        countryCodeOptions.map((opt) => [opt.value, opt.label]),
      ) as Record<string, string>,
    [],
  );

  const renderCountry = useCallback(
    (value: any) => renderValue(countryNameByCode[value] ?? value),
    [renderValue, countryNameByCode],
  );

  const countryCodeByName = useMemo(
    () =>
      Object.fromEntries(
        countryCodeOptions.map((opt) => [
          opt.label.trim().toLowerCase(),
          opt.value,
        ]),
      ) as Record<string, string>,
    [],
  );

  const resolveCountryCode = useCallback(
    (raw: string): string => {
      const trimmed = (raw || "").toString().trim();
      if (!trimmed) return "JP";
      const byName = countryCodeByName[trimmed.toLowerCase()];
      return byName ?? trimmed.toUpperCase();
    },
    [countryCodeByName],
  );

  const intervalNameByCode = useMemo(
    () =>
      Object.fromEntries(
        intervalChoiceOptions.map((opt) => [opt.code, opt.name]),
      ) as Record<string, string>,
    [intervalChoiceOptions],
  );

  const intervalCodeByName = useMemo(
    () =>
      Object.fromEntries(
        intervalChoiceOptions.map((opt) => [
          opt.name.trim().toLowerCase(),
          opt.code,
        ]),
      ) as Record<string, string>,
    [intervalChoiceOptions],
  );

  const resolveIntervalCode = useCallback(
    (raw: string): string => {
      const trimmed = (raw || "").toString().trim();
      if (!trimmed) return "";
      const byName = intervalCodeByName[trimmed.toLowerCase()];
      return byName ?? trimmed.toUpperCase();
    },
    [intervalCodeByName],
  );

  const validatePeriodicMaintenanceRow = useCallback(
    (row: EditableRow): string[] => {
      const errors: string[] = [];

      if (!row.model) errors.push(t("periodicMaintenance.missingModel"));
      if (!row.type) errors.push(t("periodicMaintenance.missingType"));
      if (!row.itemCode) errors.push(t("periodicMaintenance.missingItemCode"));
      if (!row.intervalCode)
        errors.push(t("periodicMaintenance.missingIntervalCode"));
      if (!row.work) errors.push(t("periodicMaintenance.missingWork"));
      else if (!WORK_TYPES.includes(row.work))
        errors.push(t("periodicMaintenance.invalidWork"));
      if (!row.en) errors.push(t("periodicMaintenance.missingEnglishName"));
      if (row.countryCode === "JP" && !row.ja?.trim()) {
        errors.push(t("periodicMaintenance.missingJapaneseNameForJP"));
      }
      if (
        row.countryCode &&
        !RESIDENCE_COUNTRY_CODES.includes(row.countryCode)
      ) {
        errors.push(t("periodicMaintenance.invalidCountryCode"));
      }

      return errors;
    },
    [t],
  );

  const handleRequestDeleteMasterData = (
    periodicMaintenanceMachineItemIds: string[],
  ) => {
    if (!periodicMaintenanceMachineItemIds.length) return;
    setPendingDeleteIds(periodicMaintenanceMachineItemIds);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteMasterData = async () => {
    const periodicMaintenanceMachineItemIds = pendingDeleteIds;
    if (!periodicMaintenanceMachineItemIds.length) {
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      store.dispatch(setIsLoading(true));
      const res = await apiDeletePeriodicMaintenanceMasterData(
        periodicMaintenanceMachineItemIds,
      );
      setSelectedRowKeys((prev) =>
        prev.filter((id) => !periodicMaintenanceMachineItemIds.includes(id)),
      );
      store.dispatch(
        setShowAlert(res.data?.meta?.successMessage ?? t("alreadyDeleted")),
      );
      fetchPeriodicMaintenanceMasterData();
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message ||
        t("periodicMaintenance.bulkImportError");
      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
      setDeleteConfirmOpen(false);
      setPendingDeleteIds([]);
    }
  };

  const handleOpenEditDialog = async (
    periodicMaintenanceMachineItemId: string,
  ) => {
    try {
      store.dispatch(setIsLoading(true));
      const res = await apiGetPeriodicMaintenanceMasterDataRowDetail(
        periodicMaintenanceMachineItemId,
      );
      setEditRow(res.data?.data ?? null);
      setEditErrors([]);
      setTouchedFields({});
      setEditDialogOpen(true);
    } catch (err) {
      console.error("Error fetching periodic maintenance row detail:", err);
      store.dispatch(setShowAlert(t("periodicMaintenance.bulkImportError")));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleEditFieldChange = (field: string, value: string) => {
    setEditRow((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleOpenAddDialog = () => {
    setEditRow({
      periodicMaintenanceMachineItemId: "",
      model: "",
      type: "",
      itemCode: "",
      intervalCode: intervalChoiceOptions[0]?.code ?? "",
      work: MANUAL_ENTRY_WORK_TYPES[0] ?? "",
      countryCode: "JP",
      en: "",
      ja: "",
      ar: "",
      es: "",
      fr: "",
      km: "",
      lo: "",
      pt: "",
      ru: "",
      th: "",
      ur: "",
      vi: "",
    });
    setEditErrors([]);
    setTouchedFields({});
    setEditDialogOpen(true);
  };

  const normalizeEditRow = useCallback(
    (row: PeriodicMaintenanceMasterDataRowDetail) => ({
      ...row,
      model: row.model.trim().toUpperCase(),
      type: row.type.trim().toUpperCase(),
      itemCode: row.itemCode.trim().toUpperCase(),
      intervalCode: row.intervalCode.trim().toUpperCase(),
      work: row.work.trim().toUpperCase(),
      countryCode: resolveCountryCode(row.countryCode ?? ""),
    }),
    [resolveCountryCode],
  );

  const liveEditErrors = useMemo(
    () =>
      editRow ? validatePeriodicMaintenanceRow(normalizeEditRow(editRow)) : [],
    [editRow, normalizeEditRow, validatePeriodicMaintenanceRow],
  );

  const isFieldEmpty = (value?: string) => !value || !value.trim();

  const handleSaveEdit = async () => {
    if (!editRow) return;
    const isAddMode = !editRow.periodicMaintenanceMachineItemId;

    const normalizedRow = normalizeEditRow(editRow);

    const errors = validatePeriodicMaintenanceRow(normalizedRow);
    if (errors.length) {
      setEditErrors(errors);
      return;
    }

    try {
      setEditSaving(true);
      const { periodicMaintenanceMachineItemId, ...payload } = normalizedRow;

      if (isAddMode) {
        const res = await apiPostPeriodicMaintenanceBulkImport({
          rows: [payload],
        } as any);
        const meta = res.data?.meta;
        if (meta?.failedCount) {
          const failure = meta.failedRows?.[0];
          setEditErrors([
            String(
              failure?.error ??
                failure?.reason ??
                t("periodicMaintenance.bulkImportError"),
            ),
          ]);
          return;
        }
        store.dispatch(
          setShowAlert(meta?.successMessage ?? t("formAction.add")),
        );
      } else {
        const res = await apiUpdatePeriodicMaintenanceMasterData(
          periodicMaintenanceMachineItemId,
          payload,
        );
        store.dispatch(
          setShowAlert(res.data?.meta?.successMessage ?? t("formAction.add")),
        );
      }

      setEditDialogOpen(false);
      setEditRow(null);
      fetchPeriodicMaintenanceMasterData();
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message ||
        t("periodicMaintenance.bulkImportError");
      store.dispatch(setShowAlert(message));
    } finally {
      setEditSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "checkbox",
        title: (
          <Checkbox
            checked={
              (rows?.length ?? 0) > 0 &&
              selectedRowKeys.length === (rows?.length ?? 0)
            }
            indeterminate={
              selectedRowKeys.length > 0 &&
              selectedRowKeys.length < (rows?.length ?? 0)
            }
            sx={{
              "&.Mui-checked": { color: "#140a9a" },
              "&.MuiCheckbox-indeterminate": { color: "#140a9a" },
            }}
            onChange={(e) => {
              setSelectedRowKeys(
                e.target.checked
                  ? rows.map((row) => row.periodicMaintenanceMachineItemId)
                  : [],
              );
            }}
          />
        ),
        dataIndex: "periodicMaintenanceMachineItemId",
        width: 50,
        align: "center",
        forceRender: true,
        render: (_: any, record: PeriodicMaintenanceMasterDataRow) => (
          <Checkbox
            checked={selectedRowKeys.includes(
              record.periodicMaintenanceMachineItemId,
            )}
            sx={{ "&.Mui-checked": { color: "#140a9a" } }}
            onChange={(e) => {
              const id = record.periodicMaintenanceMachineItemId;
              setSelectedRowKeys((prev) =>
                e.target.checked
                  ? [...prev, id]
                  : prev.filter((key) => key !== id),
              );
            }}
          />
        ),
      },
      {
        key: "model",
        title: "periodicMaintenance.model",
        dataIndex: "model",
        sorted: true,
        tableHeadingWhiteSpace: "nowrap",
        className: "txt-pre-wrap",
        style: { minWidth: 120 },
        render: renderValue,
      },
      {
        key: "type",
        title: "periodicMaintenance.type",
        dataIndex: "type",
        sorted: true,
        tableHeadingWhiteSpace: "nowrap",
        className: "txt-pre-wrap",
        style: { minWidth: 120 },
        render: renderValue,
      },
      {
        key: "itemName",
        title: "periodicMaintenance.itemName",
        dataIndex: "itemName",
        sorted: false,
        tableHeadingWhiteSpace: "nowrap",
        className: "txt-pre-wrap",
        style: { minWidth: 220 },
        render: renderValue,
      },
      {
        key: "intervalName",
        title: "periodicMaintenance.intervalName",
        dataIndex: "intervalName",
        sorted: false,
        tableHeadingWhiteSpace: "nowrap",
        className: "txt-pre-wrap",
        style: { minWidth: 160 },
        render: renderValue,
      },
      {
        key: "workName",
        title: "periodicMaintenance.work",
        dataIndex: "workName",
        sorted: false,
        tableHeadingWhiteSpace: "nowrap",
        className: "txt-pre-wrap",
        style: { minWidth: 160 },
        render: renderValue,
      },
      {
        key: "countryCode",
        title: "periodicMaintenance.countryCode",
        dataIndex: "countryCode",
        sorted: false,
        align: "center",
        tableHeadingWhiteSpace: "nowrap",
        className: "txt-pre-wrap",
        style: { minWidth: 160 },
        render: renderCountry,
      },
      {
        key: "actions",
        title: (
          <Button
            onClick={() => handleRequestDeleteMasterData(selectedRowKeys)}
            className="btn-no-border-red"
            color="error"
            sx={{ fontSize: "14px", fontWeight: 700, padding: "8px 12px" }}
            style={{ width: "auto", textTransform: "capitalize" }}
            disabled={selectedRowKeys.length === 0}
          >
            <DeleteOutlined />
            {t("announcementTable.delete")}
          </Button>
        ),
        dataIndex: "actions",
        forceRender: true,
        align: "center",
        style: { minWidth: 120 },
        render: (_: any, record: PeriodicMaintenanceMasterDataRow) => (
          <span style={{ display: "flex", justifyContent: "center", gap: 4 }}>
            <button
              className="edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditDialog(record.periodicMaintenanceMachineItemId);
              }}
              aria-label="Edit periodic maintenance row"
            >
              <EditOutlined />
            </button>
            <button
              className="delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestDeleteMasterData([
                  record.periodicMaintenanceMachineItemId,
                ]);
              }}
              aria-label="Delete periodic maintenance row"
            >
              <DeleteOutlined />
            </button>
          </span>
        ),
      },
    ],
    [renderValue, renderCountry, rows, selectedRowKeys, t],
  );

  const handleSort = (currentOrder: string | undefined, property: string) => {
    setOrder(currentOrder as SortOrderType | undefined);
    setOrderBy(property);
    setPage(CURRENT_PAGE);
  };

  const handleChangePage = (p: number) => {
    setPage(p);
  };

  const fetchPeriodicMaintenanceMasterData = useCallback(async () => {
    try {
      setTableLoading(true);

      const res = await apiGetPeriodicMaintenanceMasterData(
        page.toString(),
        limit.toString(),
        order,
        orderBy,
        searchText,
        work === DEFAULT_FILTER ? undefined : work,
        countryCode === DEFAULT_FILTER ? undefined : countryCode,
      );

      setRows(res.data?.data ?? []);
      setPageInfo(
        res.data?.meta?.pageInfo ?? {
          total: TOTAL_PAGE,
          page: CURRENT_PAGE,
          limit: LIMIT_PAGE,
        },
      );
    } catch (error) {
      console.error("Error fetching periodic maintenance master data:", error);
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  }, [page, limit, order, orderBy, searchText, work, countryCode]);

  useEffect(() => {
    fetchPeriodicMaintenanceMasterData();
  }, [fetchPeriodicMaintenanceMasterData]);

  useEffect(() => {
    if (searchInput === "" && searchText !== "") {
      setPage(CURRENT_PAGE);
      setSearchText("");
    }
  }, [searchInput, searchText]);

  const handleDownloadTemplate = async () => {
    try {
      store.dispatch(setIsLoading(true));

      const templateResponse = await apiGetPeriodicMaintenanceTemplateFileUrl();
      const fileUrl =
        templateResponse?.data?.data?.periodicMaintenanceTemplatePathUrl;

      if (!fileUrl) {
        store.dispatch(setShowAlert(t("periodicMaintenance.downloadError")));
        console.error("No URL found to download");
        return;
      }

      const fileResponse = await fetch(fileUrl);
      if (!fileResponse.ok) throw new Error("Failed to fetch file");
      const arrayBuffer = await fileResponse.arrayBuffer();

      const workbook = new Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.getWorksheet(TEMPLATE_SHEET_NAME);

      if (!worksheet) {
        store.dispatch(setShowAlert(t("periodicMaintenance.downloadError")));
        return;
      }

      worksheet.unprotect();

      const headerRow = worksheet.getRow(1);
      headerRow.getCell(1).value = t("periodicMaintenance.model");
      headerRow.getCell(2).value = t("periodicMaintenance.type");
      headerRow.getCell(3).value = t("periodicMaintenance.itemCode");
      headerRow.getCell(4).value = t("periodicMaintenance.intervalCode");
      headerRow.getCell(5).value = t("periodicMaintenance.work");
      headerRow.getCell(6).value = t("periodicMaintenance.countryCode");
      LOCALE_COLUMNS.forEach(({ labelKey }, i) => {
        headerRow.getCell(BASE_COLUMN_KEYS.length + 1 + i).value = t(labelKey);
      });
      headerRow.commit();

      const existingLegendSheet = workbook.getWorksheet(
        TEMPLATE_LEGEND_SHEET_NAME,
      );
      if (existingLegendSheet) {
        workbook.removeWorksheet(existingLegendSheet.id);
      }
      const legendSheet = workbook.addWorksheet(TEMPLATE_LEGEND_SHEET_NAME, {
        state: "hidden",
      });
      legendSheet.addRow([
        t("periodicMaintenance.countryCode"),
        t("periodicMaintenance.countryName"),
      ]);
      countryCodeOptions.forEach((opt) =>
        legendSheet.addRow([opt.value, opt.label]),
      );

      const existingIntervalLegendSheet = workbook.getWorksheet(
        TEMPLATE_INTERVAL_LEGEND_SHEET_NAME,
      );
      if (existingIntervalLegendSheet) {
        workbook.removeWorksheet(existingIntervalLegendSheet.id);
      }
      const intervalLegendSheet = workbook.addWorksheet(
        TEMPLATE_INTERVAL_LEGEND_SHEET_NAME,
        { state: "hidden" },
      );
      intervalLegendSheet.addRow([
        t("periodicMaintenance.intervalCode"),
        t("periodicMaintenance.intervalName"),
      ]);
      intervalChoiceOptions.forEach((opt) =>
        intervalLegendSheet.addRow([opt.code, opt.name]),
      );

      for (
        let rowNumber = 2;
        rowNumber <= TEMPLATE_DATA_ROW_COUNT;
        rowNumber++
      ) {
        const row = worksheet.getRow(rowNumber);

        for (let col = 1; col <= COLUMN_ORDER.length; col++) {
          row.getCell(col).protection = { locked: false };
        }

        row.getCell(COUNTRY_CODE_COLUMN_INDEX).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [
            `=OFFSET('${TEMPLATE_LEGEND_SHEET_NAME}'!$B$2,0,0,COUNTA('${TEMPLATE_LEGEND_SHEET_NAME}'!$B:$B)-1,1)`,
          ],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: t("periodicMaintenance.invalidCountryCode"),
          error: t("periodicMaintenance.invalidCountryCode"),
        };

        const countryCell = row.getCell(COUNTRY_CODE_COLUMN_INDEX);
        const currentValue = (countryCell.value ?? "").toString().trim();
        if (currentValue && countryNameByCode[currentValue.toUpperCase()]) {
          countryCell.value = countryNameByCode[currentValue.toUpperCase()];
        }

        row.getCell(INTERVAL_CODE_COLUMN_INDEX).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [
            `=OFFSET('${TEMPLATE_INTERVAL_LEGEND_SHEET_NAME}'!$B$2,0,0,COUNTA('${TEMPLATE_INTERVAL_LEGEND_SHEET_NAME}'!$B:$B)-1,1)`,
          ],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: t("periodicMaintenance.invalidIntervalCode"),
          error: t("periodicMaintenance.invalidIntervalCode"),
        };

        const intervalCell = row.getCell(INTERVAL_CODE_COLUMN_INDEX);
        const currentIntervalValue = (intervalCell.value ?? "")
          .toString()
          .trim();
        if (
          currentIntervalValue &&
          intervalNameByCode[currentIntervalValue.toUpperCase()]
        ) {
          intervalCell.value =
            intervalNameByCode[currentIntervalValue.toUpperCase()];
        }

        row.getCell(WORK_COLUMN_INDEX).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [
            `"${MANUAL_ENTRY_WORK_TYPES.map((w) => capitalize(w)).join(",")}"`,
          ],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: t("periodicMaintenance.invalidWork"),
          error: t("periodicMaintenance.invalidWork"),
        };

        row.commit();
      }

      worksheet.protect(TEMPLATE_PASSWORD, {
        selectLockedCells: false,
        selectUnlockedCells: true,
      });
      legendSheet.protect(TEMPLATE_PASSWORD, {});
      intervalLegendSheet.protect(TEMPLATE_PASSWORD, {});

      const updatedBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([updatedBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "periodic-maintenance-template.xlsx";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      store.dispatch(setShowAlert(t("periodicMaintenance.downloadError")));
      console.error("Error downloading template:", error);
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const markDuplicateRows = (parsedRows: PreviewRow[]): PreviewRow[] => {
    const seenKeys = new Set<string>();

    return parsedRows.map((row) => {
      const key = [
        row.model,
        row.type,
        row.itemCode,
        row.intervalCode,
        row.work,
        row.countryCode,
      ].join("-");

      let isDuplicate = false;
      const errors = [...row._errors];

      if (seenKeys.has(key)) {
        isDuplicate = true;
        errors.push(t("periodicMaintenance.duplicateRow"));
      } else {
        seenKeys.add(key);
      }

      return { ...row, _isDuplicate: isDuplicate, _errors: errors };
    });
  };

  const handleFileExcelRead = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = [".xlsx", ".xls"];
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();

    if (!validExtensions.includes(ext)) {
      store.dispatch(
        setShowAlert(t("InvalidfiletypePleaseuploadanExcelfilexlsxorxls")),
      );
      return;
    }

    try {
      store.dispatch(setIsLoading(true));

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const rawRows = XLSX.utils.sheet_to_json<any[]>(sheet, {
        header: 1,
        range: 1,
        defval: "",
        raw: false,
      });

      const nonEmptyRows = rawRows.filter((r) =>
        r.some((cell) => cell !== "" && cell !== undefined && cell !== null),
      );

      const parsed: PreviewRow[] = nonEmptyRows.map((r, index) => {
        const getColumn = (key: string) => {
          const colIndex = COLUMN_ORDER.indexOf(key);
          return colIndex === -1 ? "" : (r[colIndex] ?? "").toString();
        };

        const model = getColumn("model").trim().toUpperCase();
        const type = getColumn("type").trim().toUpperCase();
        const itemCode = getColumn("itemCode").trim().toUpperCase();
        const intervalCode = resolveIntervalCode(getColumn("intervalCode"));
        const work = getColumn("work").trim().toUpperCase();
        const countryCode = resolveCountryCode(getColumn("countryCode"));
        const en = getColumn("en");
        const ja = getColumn("ja");

        const rowErrors = validatePeriodicMaintenanceRow({
          model,
          type,
          itemCode,
          intervalCode,
          work,
          countryCode,
          en,
          ja,
        });

        const localeValues: Record<string, string> = {};
        LOCALE_COLUMNS.forEach(({ key }) => {
          localeValues[key] = getColumn(key);
        });

        return {
          id: index,
          model,
          type,
          itemCode,
          intervalCode,
          work,
          countryCode,
          ...localeValues,
          _errors: rowErrors,
          _isDuplicate: false,
        };
      });

      setPreviewRows(markDuplicateRows(parsed));
      setPreviewOpen(true);
      setFailedCount(0);
    } catch (err) {
      console.error(err);
      store.dispatch(
        setShowAlert(t("periodicMaintenance.failedToReadExcelFile")),
      );
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleBulkImport = async () => {
    const validRows = previewRows.filter(
      (row) => row._errors.length === 0 && !row._isDuplicate,
    );
    if (!validRows.length) return;

    try {
      store.dispatch(setIsLoading(true));

      const payload = {
        rows: validRows.map((row) => {
          const localeValues: Record<string, string | undefined> = {};
          LOCALE_COLUMNS.forEach(({ key }) => {
            localeValues[key] = row[key]?.toString().trim() || undefined;
          });

          return {
            model: row.model,
            type: row.type,
            itemCode: row.itemCode,
            intervalCode: row.intervalCode,
            work: row.work,
            countryCode: row.countryCode,
            ...localeValues,
          };
        }),
      };

      const res = await apiPostPeriodicMaintenanceBulkImport(payload as any);
      const result = res.data?.data;
      const meta = res.data?.meta;

      store.dispatch(
        setShowAlert(
          `${meta?.successMessage ?? ""} (${t(
            "periodicMaintenance.bulkImportSummary",
            {
              success: meta?.successCount ?? 0,
              failed: meta?.failedCount ?? 0,
            },
          )})`,
        ),
      );

      if (meta?.failedRows?.length) {
        const failedMap = new Map<string, string>(
          meta.failedRows.map((f: any) => [
            [
              f.row?.model,
              f.row?.type,
              f.row?.itemCode,
              f.row?.intervalCode,
              f.row?.work,
              f.row?.countryCode,
            ].join("-"),
            String(f.error ?? f.reason ?? ""),
          ]),
        );

        setPreviewRows((prev) =>
          prev
            .filter((row) =>
              failedMap.has(
                [
                  row.model,
                  row.type,
                  row.itemCode,
                  row.intervalCode,
                  row.work,
                  row.countryCode,
                ].join("-"),
              ),
            )
            .map((row) => ({
              ...row,
              _isFailed: true,
              _failureReason: failedMap.get(
                [
                  row.model,
                  row.type,
                  row.itemCode,
                  row.intervalCode,
                  row.work,
                  row.countryCode,
                ].join("-"),
              ),
            })),
        );

        setFailedCount(meta.failedCount ?? meta.failedRows.length);
      } else {
        setFailedCount(0);
        setPreviewOpen(false);
        setBulkSearch("");
        fetchPeriodicMaintenanceMasterData();
      }

      void result;
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message ||
        t("periodicMaintenance.bulkImportError");

      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const previewColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "model",
        headerName: t("periodicMaintenance.model"),
        width: 100,
      },
      { field: "type", headerName: t("periodicMaintenance.type"), width: 100 },
      {
        field: "itemCode",
        headerName: t("periodicMaintenance.itemCode"),
        width: 160,
      },
      {
        field: "intervalCode",
        headerName: t("periodicMaintenance.intervalCode"),
        width: 130,
      },
      { field: "work", headerName: t("periodicMaintenance.work"), width: 130 },
      {
        field: "countryCode",
        headerName: t("periodicMaintenance.countryCode"),
        width: 160,
        renderCell: (params: any) =>
          countryNameByCode[params.value] ?? params.value,
      },
      ...LOCALE_COLUMNS.map(({ key, labelKey }) => ({
        field: key,
        headerName: t(labelKey),
        width: 180,
        renderCell:
          key === "ar" || key === "ur"
            ? (params: any) => (
                <div dir="rtl" style={{ textAlign: "right", width: "100%" }}>
                  {params.value || "-"}
                </div>
              )
            : undefined,
      })),
      {
        field: "_errors",
        headerName: t("periodicMaintenance.validationErrors"),
        width: 280,
        renderCell: (params) => {
          const errors: string[] = params.value || [];
          if (!errors.length) {
            return (
              <div
                style={{ width: "100%", textAlign: "center", color: "#9CA3AF" }}
              >
                -
              </div>
            );
          }
          return (
            <span style={{ color: "#991B1B", fontWeight: 500 }}>
              {errors.join(", ")}
            </span>
          );
        },
      },
      {
        field: "_failureReason",
        headerName: t("failureReason"),
        width: 280,
        renderCell: (params) => {
          if (!params.value) {
            return (
              <div
                style={{ width: "100%", textAlign: "center", color: "#9CA3AF" }}
              >
                -
              </div>
            );
          }
          return (
            <span style={{ color: "#991B1B", fontWeight: 500 }}>
              {params.value}
            </span>
          );
        },
      },
    ],
    [t, countryNameByCode],
  );

  return (
    <>
      <Helmet title={t("metaInfo.managePeriodicMaintenance")} />

      <Box
        className="container"
        sx={{ mx: { xs: 2, sm: 3, md: 5, xl: "auto" }, px: "24px" }}
      >
        <Box sx={{ py: 4, margin: "auto" }}>
          <Grid container>
            <Grid item xs={12}>
              <PageTitle title="managePeriodicMaintenance" className="mb-25" />
            </Grid>

            <Grid item xs={12} sm={6} md={6} sx={{ mt: { xs: 1, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <FormControl
                  size="small"
                  fullWidth={isSmallScreen}
                  sx={{ minWidth: { sm: 180 } }}
                >
                  <InputLabel id="work-filter-label">
                    {t("periodicMaintenance.work")}
                  </InputLabel>
                  <Select
                    labelId="work-filter-label"
                    label={t("periodicMaintenance.work")}
                    value={work}
                    onChange={(e) => {
                      setPage(CURRENT_PAGE);
                      setWork(e.target.value);
                    }}
                  >
                    <MenuItem value={DEFAULT_FILTER}>{t("all")}</MenuItem>
                    {WORK_TYPES.map((w) => (
                      <MenuItem key={w} value={w}>
                        {capitalize(w)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  fullWidth={isSmallScreen}
                  sx={{ minWidth: { sm: 180 } }}
                >
                  <InputLabel id="country-filter-label">
                    {t("periodicMaintenance.countryCode")}
                  </InputLabel>
                  <Select
                    labelId="country-filter-label"
                    label={t("periodicMaintenance.countryCode")}
                    value={countryCode}
                    onChange={(e) => {
                      setPage(CURRENT_PAGE);
                      setCountryCode(e.target.value);
                    }}
                  >
                    <MenuItem value={DEFAULT_FILTER}>{t("all")}</MenuItem>
                    {countryCodeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6} sx={{ mt: { xs: 1, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  fullWidth={isSmallScreen}
                  variant="contained"
                  className="btn-add-translation"
                  onClick={handleOpenAddDialog}
                >
                  <IconPlus />
                  <span style={{ marginLeft: 8 }}>{t("formAction.add")}</span>
                </Button>

                <Button
                  fullWidth={isSmallScreen}
                  startIcon={<MoreVert />}
                  className="btn btn-no-border-dark-blue"
                  style={{
                    height: "45px",
                    fontSize: "14px",
                  }}
                  sx={{
                    "& .MuiButton-startIcon": {
                      marginInlineEnd: "4px",
                    },
                  }}
                  onClick={(e) => setOptionsMenuAnchor(e.currentTarget)}
                >
                  {t("formAction.excelOptions")}
                </Button>

                <Menu
                  anchorEl={optionsMenuAnchor}
                  open={openOptionsMenu}
                  onClose={handleCloseOptionsMenu}
                >
                  <MenuItem
                    onClick={() => {
                      handleCloseOptionsMenu();
                      handleDownloadTemplate();
                    }}
                  >
                    <DownloadOutlined sx={{ mr: 1 }} />
                    {t("formAction.downloadexcel")}
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleCloseOptionsMenu();
                      fileInputRef.current?.click();
                    }}
                  >
                    <UploadOutlined sx={{ mr: 1 }} />
                    {t("formAction.uploadexcel")}
                  </MenuItem>
                </Menu>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, .xls"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleFileExcelRead(e);
                    e.target.value = "";
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  mt: { xs: 1, sm: 2 },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: { xs: "100%", sm: 277 },
                  }}
                >
                  <InputText
                    ref={searchRef}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setPage(CURRENT_PAGE);
                        setSearchText(searchInput.trim());
                      }
                    }}
                    placeholder={t("Customermachinenumber")}
                    className="custom-input"
                    style={{
                      width: "100%",
                      height: 50,
                      borderRadius: 30,
                      border: "1.5px solid #bcbcbc",
                      padding: "0 2rem 0 1rem",
                    }}
                  />

                  {searchInput && (
                    <button
                      className="clear-button"
                      type="button"
                      onClick={() => {
                        setSearchInput(DEFAULT_SEARCH_INPUT);
                        setSearchText(DEFAULT_SEARCH_TEXT);
                        setPage(CURRENT_PAGE);
                        setTimeout(() => searchRef.current?.focus(), 0);
                      }}
                    >
                      ×
                    </button>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <DataTable
              rowKey="periodicMaintenanceMachineItemId"
              columns={columns}
              dataSource={rows}
              textEmptyData="periodicMaintenance.noDataFound"
              onRequestSort={handleSort}
              loading={tableLoading}
            />

            <DataPagination
              isHidden={!rows?.length}
              className="mt-25 sp-p24"
              currentPage={pageInfo.page}
              limitPage={pageInfo.limit}
              totalPage={pageInfo.total}
              handleChangePage={handleChangePage}
            />
          </Box>
        </Box>
      </Box>

      <DataDialog
        disableBackdropClick
        open={previewOpen}
        setOpen={setPreviewOpen}
        title={t("periodicMaintenance.previewTitle")}
        className="dialog-inspection-form"
      >
        {(hasBlockingRows || failedCount > 0) && (
          <div
            style={{
              background: "#FEE2E2",
              color: "#991B1B",
              padding: "12px 16px",
              borderRadius: 8,
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              fontWeight: 500,
            }}
          >
            ⚠{" "}
            {hasBlockingRows
              ? t("periodicMaintenance.previewHasErrors")
              : t("periodicMaintenance.previewFailedWarning", {
                  count: failedCount,
                })}
          </div>
        )}

        <Box sx={{ position: "relative", width: "100%", mb: 2 }}>
          <SearchOutlined
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9CA3AF",
              pointerEvents: "none",
            }}
          />
          <InputText
            value={bulkSearch}
            onChange={(e) => setBulkSearch(e.target.value)}
            placeholder={t("Customermachinenumber")}
            className="custom-input"
            style={{
              width: "100%",
              height: 50,
              borderRadius: 30,
              border: "1.5px solid #bcbcbc",
              padding: "0 1rem 0 3rem",
            }}
          />
        </Box>

        <div
          style={{
            width: "100%",
            height: 500,
            overflow: "auto",
            marginBottom: 20,
          }}
        >
          <DataGrid
            rows={filteredPreviewRows}
            columns={previewColumns}
            disableColumnMenu
            pagination
            paginationModel={bulkPaginationModel}
            onPaginationModelChange={setBulkPaginationModel}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            getRowClassName={(params) =>
              params.row._isFailed ||
              params.row._isDuplicate ||
              params.row._errors?.length
                ? "row-duplicate"
                : ""
            }
            sx={{
              "& .row-duplicate": {
                backgroundColor: "#ffe5e5",
                "&:hover": { backgroundColor: "#ffd6d6" },
              },
            }}
          />
        </div>

        <div
          className="btn-group-footer mt-8"
          style={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            background: "#fff",
            paddingTop: "16px",
            paddingBottom: "8px",
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setPreviewOpen(false);
              setBulkSearch("");
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            disabled={hasBlockingRows}
            onClick={handleBulkImport}
          >
            {t("periodicMaintenance.confirmImport")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick
        open={editDialogOpen}
        setOpen={setEditDialogOpen}
        title={
          editRow?.periodicMaintenanceMachineItemId
            ? t("periodicMaintenance.editRowTitle")
            : t("periodicMaintenance.addRowTitle")
        }
        className="dialog-inspection-form"
        dialogActions={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              width: "100%",
            }}
          >
            <Button
              className="btn btn-no-border"
              onClick={() => setCancelEditConfirmOpen(true)}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary"
              disabled={editSaving || liveEditErrors.length > 0}
              onClick={handleSaveEdit}
            >
              {t("formAction.add")}
            </Button>
          </div>
        }
      >
        {editRow && (
          <>
            {editErrors.length > 0 && (
              <div
                style={{
                  background: "#FEE2E2",
                  color: "#991B1B",
                  padding: "12px 16px",
                  borderRadius: 8,
                  marginBottom: 12,
                  fontWeight: 500,
                }}
              >
                {editErrors.join(", ")}
              </div>
            )}

            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  className="input-field"
                  fullWidth
                  required
                  sx={FIELD_BORDER_TRANSITION_SX}
                  error={touchedFields.model && isFieldEmpty(editRow.model)}
                  label={t("periodicMaintenance.model")}
                  value={editRow.model}
                  onChange={(e) =>
                    handleEditFieldChange("model", e.target.value)
                  }
                  onBlur={() => markFieldTouched("model")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className="input-field"
                  fullWidth
                  required
                  sx={FIELD_BORDER_TRANSITION_SX}
                  error={touchedFields.type && isFieldEmpty(editRow.type)}
                  label={t("periodicMaintenance.type")}
                  value={editRow.type}
                  onChange={(e) =>
                    handleEditFieldChange("type", e.target.value)
                  }
                  onBlur={() => markFieldTouched("type")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className="input-field"
                  fullWidth
                  required
                  sx={FIELD_BORDER_TRANSITION_SX}
                  error={
                    touchedFields.itemCode && isFieldEmpty(editRow.itemCode)
                  }
                  label={t("periodicMaintenance.itemCode")}
                  value={editRow.itemCode}
                  onChange={(e) =>
                    handleEditFieldChange("itemCode", e.target.value)
                  }
                  onBlur={() => markFieldTouched("itemCode")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  className="select-field"
                  fullWidth
                  required
                  error={
                    touchedFields.intervalCode &&
                    isFieldEmpty(editRow.intervalCode)
                  }
                  sx={SELECT_FIELD_SX}
                >
                  <InputLabel id="edit-interval-label" required>
                    {t("periodicMaintenance.intervalCode")}
                  </InputLabel>
                  <Select
                    labelId="edit-interval-label"
                    label={t("periodicMaintenance.intervalCode")}
                    value={editRow.intervalCode}
                    onChange={(e) =>
                      handleEditFieldChange("intervalCode", e.target.value)
                    }
                    onBlur={() => markFieldTouched("intervalCode")}
                  >
                    {/* If this row's current code isn't in the active choice
                        list (e.g. it's since been disabled), keep it
                        selectable so saving doesn't silently change it. */}
                    {!intervalChoiceOptions.some(
                      (opt) => opt.code === editRow.intervalCode,
                    ) && (
                      <MenuItem value={editRow.intervalCode}>
                        {editRow.intervalCode}
                      </MenuItem>
                    )}
                    {intervalChoiceOptions.map((opt) => (
                      <MenuItem key={opt.code} value={opt.code}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  className="select-field"
                  fullWidth
                  required
                  error={touchedFields.work && isFieldEmpty(editRow.work)}
                  sx={SELECT_FIELD_SX}
                >
                  <InputLabel id="edit-work-label" required>
                    {t("periodicMaintenance.work")}
                  </InputLabel>
                  <Select
                    labelId="edit-work-label"
                    label={t("periodicMaintenance.work")}
                    value={editRow.work}
                    onChange={(e) =>
                      handleEditFieldChange("work", e.target.value)
                    }
                    onBlur={() => markFieldTouched("work")}
                  >
                    {/* Keeps a row already saved as REFILL (e.g. via Excel
                        import) selectable here too, so opening it for edit
                        doesn't silently change its work type. */}
                    {!MANUAL_ENTRY_WORK_TYPES.includes(editRow.work) && (
                      <MenuItem value={editRow.work}>
                        {capitalize(editRow.work)}
                      </MenuItem>
                    )}
                    {MANUAL_ENTRY_WORK_TYPES.map((w) => (
                      <MenuItem key={w} value={w}>
                        {capitalize(w)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  className="select-field"
                  fullWidth
                  required
                  error={
                    touchedFields.countryCode &&
                    isFieldEmpty(editRow.countryCode)
                  }
                  sx={SELECT_FIELD_SX}
                >
                  <InputLabel id="edit-country-label" required>
                    {t("periodicMaintenance.countryCode")}
                  </InputLabel>
                  <Select
                    labelId="edit-country-label"
                    label={t("periodicMaintenance.countryCode")}
                    value={editRow.countryCode}
                    onChange={(e) =>
                      handleEditFieldChange("countryCode", e.target.value)
                    }
                    onBlur={() => markFieldTouched("countryCode")}
                  >
                    {countryCodeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {DIALOG_LOCALE_COLUMNS.map(({ key, labelKey }) => {
                const isRtl = key === "ar" || key === "ur";
                const field = (
                  <TextField
                    className="input-field"
                    fullWidth
                    sx={FIELD_BORDER_TRANSITION_SX}
                    label={t(labelKey)}
                    value={(editRow as any)[key] ?? ""}
                    required={key === "en"}
                    error={
                      key === "en" &&
                      touchedFields.en &&
                      isFieldEmpty((editRow as any).en)
                    }
                    dir={isRtl ? "rtl" : undefined}
                    onChange={(e) => handleEditFieldChange(key, e.target.value)}
                    onBlur={
                      key === "en" ? () => markFieldTouched("en") : undefined
                    }
                  />
                );

                return (
                  <Grid item xs={12} sm={6} key={key}>
                    {isRtl ? (
                      <CacheProvider value={rtlFieldCache}>
                        <ThemeProvider theme={rtlFieldTheme}>
                          {field}
                        </ThemeProvider>
                      </CacheProvider>
                    ) : (
                      field
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </DataDialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        typeDialog={DialogTypes.ERROR}
        title="periodicMaintenance.confirmDeleteTitle"
        content={
          pendingDeleteIds.length > 1
            ? "periodicMaintenance.confirmDeleteMultipleContent"
            : "periodicMaintenance.confirmDeleteContent"
        }
        btnSubmitText="formAction.delete"
        classBtnSubmit="btn-primary bg-red"
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteIds([]);
        }}
        onSubmit={handleConfirmDeleteMasterData}
      />

      <ConfirmDialog
        open={cancelEditConfirmOpen}
        setOpen={setCancelEditConfirmOpen}
        title="periodicMaintenance.confirmCancelEditTitle"
        content="periodicMaintenance.confirmCancelEditContent"
        btnSubmitText="ok"
        classBtnSubmit="btn-primary"
        onCancel={() => setCancelEditConfirmOpen(false)}
        onSubmit={() => {
          setCancelEditConfirmOpen(false);
          setEditDialogOpen(false);
          setEditRow(null);
          setEditErrors([]);
        }}
      />
    </>
  );
}
